import { toApplicationData } from '@data/api/mappers/workflowData/toApplicationData'
import { toApplicationReviewData } from '@data/api/mappers/workflowData/toApplicationReviewData'
import { toBasicRegistrationData } from '@data/api/mappers/workflowData/toBasicRegistrationData'
import { toConfirmEmailData } from '@data/api/mappers/workflowData/toConfirmEmailData'
import { toInterestsData } from '@data/api/mappers/workflowData/toInterestsData'
import { toLocalCommunityData } from '@data/api/mappers/workflowData/toLocalCommunityData'
import { toMembershipData } from '@data/api/mappers/workflowData/toMembershipData'
import { toPhotoData } from '@data/api/mappers/workflowData/toPhotoData'
import { requester, RequestInfo } from '@data/api/requester'
import { QueryOptions } from '@data/queries/types'
import { useQuery, UseQueryResult } from '@tanstack/react-query'

export type WorkflowStep =
    | 'basic'
    | 'photo'
    | 'interests'
    | 'membership'
    | 'localcommunity'
    | 'application-review'
    | 'application'
    | 'basic-registration'
    | 'confirm-email'

export type Data =
    | TBasicStepWorkflowData
    | TConfirmEmailWorkflowData
    | ApplicationReviewWorkflowData
    | PhotoWorkflowData
    | InterestsWorkflowData
    | MembershipWorkflowData
    | TLocalcommunityWorkflowData
    | TApplicationWorkflowData

const photoRequest: RequestInfo<PhotoWorkflowData> = {
    method: 'GET',
    url: '/registration/photo',
    responseMapper: toPhotoData,
}

const interestsRequest: RequestInfo<InterestsWorkflowData> = {
    method: 'GET',
    url: '/registration/interests',
    responseMapper: toInterestsData,
}

const membershipRequest: RequestInfo<MembershipWorkflowData> = {
    method: 'GET',
    url: '/registration/membership',
    responseMapper: toMembershipData,
}

const localCommunityRequest: RequestInfo<TLocalcommunityWorkflowData> = {
    method: 'GET',
    url: '/registration/local-community',
    responseMapper: toLocalCommunityData,
}

const applicationRequest: RequestInfo<TApplicationWorkflowData> = {
    method: 'GET',
    url: '/registration/application',
    responseMapper: toApplicationData,
}

const applicationReviewRequest: RequestInfo<ApplicationReviewWorkflowData> = {
    method: 'PATCH',
    url: '/registration/application-review',
    responseMapper: toApplicationReviewData,
}

const basicRegistrationRequest: RequestInfo<TBasicStepWorkflowData> = {
    method: 'GET',
    url: '/registration/basic',
    responseMapper: toBasicRegistrationData,
    params:
        typeof window !== 'undefined'
            ? Object.fromEntries(new URLSearchParams(window.location.search).entries())
            : undefined,
}

const confirmEmailRequest: RequestInfo<TConfirmEmailWorkflowData> = {
    method: 'GET',
    url: '/registration/confirm-email',
    responseMapper: toConfirmEmailData,
}

const STEP_TO_REQUEST: Record<WorkflowStep, () => Promise<Data> | undefined> = {
    photo: () => requester(photoRequest),
    interests: () => requester(interestsRequest),
    localcommunity: () => requester(localCommunityRequest),
    'application-review': () => requester(applicationReviewRequest),
    application: () => requester(applicationRequest),
    basic: () => requester(basicRegistrationRequest),
    membership: () => requester(membershipRequest),
    'confirm-email': () => requester(confirmEmailRequest),
    'basic-registration': () => undefined,
}

export type RegistrationWorkflowNavigatorOptions = QueryOptions<Data, ApiError>

export const useRegistrationWorkflowNavigator = (
    step: WorkflowStep,
    options?: RegistrationWorkflowNavigatorOptions
): UseQueryResult<Data, ApiError> => {
    const queryKey = ['registration:workflow', { step }]
    const queryFn = () => STEP_TO_REQUEST[step]() ?? requester(membershipRequest)

    return useQuery<Data, ApiError>({
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        ...options,
        queryKey,
        queryFn,
    })
}
